import { useState } from 'react'
import * as Yup from 'yup'
import { Accordion } from '../../components/accordion'
import { ImageUploadField } from '../../components/images/image-upload-field'
import ComboBox from '../../components/inputs/combobox'
import { TextInput } from '../../components/inputs/textInput'
import { AddLocationModal } from '../../components/modal/app_modals/addLocationModal'
import CustSwitch from '../../components/switch'
import {
  CreateFulfilmentCenter,
  FulfilmentCentre,
  FulfilmentCentreRole,
} from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { addFulfilmentCenterSchema } from '../../validation/validate'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  onClose: () => void
  centres: FulfilmentCentre[]
  loading: boolean
  onSave: (values: CreateFulfilmentCenter) => Promise<boolean>
}

const parentSchema = Yup.object({
  name: Yup.string().trim().required('Fulfilment name is required'),
  domain: Yup.string()
    .trim()
    .required('Domain is required for a parent storefront'),
})

export function FulfilmentCentreForm({
  open,
  setOpen,
  onClose,
  centres,
  loading,
  onSave,
}: Props) {
  const [role, setRole] = useState<'PARENT' | 'CHILD'>('CHILD')
  const isParent = role === 'PARENT'

  return (
    <AddLocationModal
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        setRole('CHILD')
        onClose()
      }}
      title="Add Fulfilment Center"
      fields={isParent ? ['name'] : ['name', 'address', 'coordinates']}
      additionalFields={{
        domain: '',
        parentFulfilmentCentre: '',
        isActive: true,
        image: '',
        config: { food: true, lifestyle: true, event: true },
      }}
      schema={isParent ? parentSchema : addFulfilmentCenterSchema}
      loading={loading}
      placeholderPrefix="Fulfilment Center"
      showLocationPicker={!isParent}
      header={
        <ComboBox
          title="Centre type"
          aria-label="Centre type"
          name="role"
          value={isParent ? 'Parent storefront' : 'Physical fulfilment centre'}
          menuItems={[
            { name: 'Physical fulfilment centre', value: 'CHILD' },
            { name: 'Parent storefront', value: 'PARENT' },
          ]}
          onSelectOption={setRole}
          onChange={() => {}}
          readOnly
          hasIcon={false}
          isDropdown
          immediate
          containerClassName="mb-6"
        />
      }
      onSuccessCallback={async (values, resetForm) => {
        const saved = await onSave({
          name: values.name.trim(),
          role,
          domain: values.domain.trim() || undefined,
          parentFulfilmentCentre: isParent
            ? null
            : values.parentFulfilmentCentre || null,
          isActive: values.isActive,
          image: values.image || undefined,
          config: values.config,
          ...(isParent
            ? {}
            : {
                address: values.address,
                coordinates: [Number(values.long), Number(values.lat)],
              }),
        })
        if (saved) {
          resetForm()
          setRole('CHILD')
        }
      }}
    >
      {({ formik }) => (
        <>
          <ImageUploadField
            imageUrl={formik.values.image}
            onChange={(url) => formik.setFieldValue('image', url ?? '')}
          />
          <TextInput
            type="text"
            name="domain"
            placeholder="Domain"
            value={formik.values.domain}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            errors={
              formik.touched.domain && formik.errors.domain
                ? String(formik.errors.domain)
                : ''
            }
            hasIcon={false}
            className="!border-divider-gray"
          />
          <p className="text-sm text-sec-black">
            {isParent
              ? 'All active physical fulfilment centres are included automatically, including new centres.'
              : `Automatically available through ${
                  centres.find(
                    (centre) =>
                      centre.role === FulfilmentCentreRole.PARENT &&
                      centre.isActive !== false,
                  )?.name ?? 'the parent storefront'
                } when active. No parent assignment is needed.`}
          </p>
          <div className="flex items-center justify-between gap-3 border rounded-lg px-4 py-3">
            <p className="font-medium">Active</p>
            <CustSwitch
              checked={formik.values.isActive}
              onChange={(checked) => formik.setFieldValue('isActive', checked)}
            />
          </div>
          <Accordion
            title="Config"
            className="[&>button>p]:!font-medium [&>button>p]:!text-base"
          >
            <div className="flex flex-col gap-3">
              {(['lifestyle', 'event', 'food'] as const).map((section) => (
                <div
                  key={section}
                  className="flex items-center justify-between gap-3 border rounded-lg px-4 py-3"
                >
                  <p className="font-medium capitalize">{section}</p>
                  <CustSwitch
                    checked={formik.values.config[section]}
                    onChange={(checked) =>
                      formik.setFieldValue(`config.${section}`, checked)
                    }
                  />
                </div>
              ))}
            </div>
          </Accordion>
        </>
      )}
    </AddLocationModal>
  )
}
